<template>
<div class="row">
        <div class="col-md-12">
            <h3>WebHooks</h3>
            <div class="row">
                <div class="col-md-12">
                    <div>Webhooks allow you to define a URL where CINC will send information about leads and other events in near real-time.</div>
                    <div>Each token you own can define an endpoint where you'd like event payloads to be sent.</div>
                    <div>If you have not yet setup your API token, please see the <router-link to="/docs/intro">setup</router-link> help documentation first.</div>
                </div>
            </div>

            <div class="row mt20">
                <div class="col-md-12">
                    <h6>Create a Webhook</h6>
                    <div>Each token may define a single endpoint to receive events via a <span class="keyword">POST</span> call.</div>
                    <div>If you already have a token, setting up a webhook endpoint is a single API call.</div>
                    <div>
                        A webhook can be configured via the <span class="keyword">/token/webhook</span> API endpoint.
                    </div>
                </div>
            </div>

            <div class="row mt20">
                <div class="col-md-12">
                    <h6>Event Description</h6>
                    <div>
                        Each event has a <span class="keyword">basis</span>; that is, a reason for the event to have occurred.
                    </div>
                    <div>
                        For example, if a label is applied to a lead, an event with a <span class="keyword">Lead</span> basis will be generated and sent to the <span class="keyword">URL</span> defined in your API token.
                    </div>
                    <div>
                        Along with the basis of the event, the <span class="keyword">basisId</span> is also included.  In the case of a Lead, the basisId is the same as the <span class="keyword">leadId</span> of the lead itself.
                    </div>

                    <div class="mt15">
                        Each event also has a <span class="keyword">subject</span>.  The subject of the event describes the type of entity involved in the event basis.
                    </div>
                    <div>
                        Continuing with our example from above, applying a label to a lead would result in an event with a <span class="keyword">`Label`</span> subject.
                    </div>
                    <div>
                        An event may or may not contain a <span class="keyword">subjectId</span> depending on what the subject of the event is.
                    </div>
                    <div>
                        For example, if the subject is <span class="keyword">`Label`</span>,
                        the subject Id would be the <span class="keyword">labelId</span> of the label that was applied to the lead.
                    </div>

                    <div class="mt15">
                        How each event's <span class="keyword">subject</span> pertains to its <span class="keyword">basis</span> is described by the event's <span class="keyword">action</span>.
                    </div>
                    <div>
                        In our Lead Label example, the <span class="keyword">action</span> is <span class="keyword">`applied`</span>, since an existing label was applied to a lead.
                    </div>

                    <div class="mt15">
                        Combining basis, subject, and action attributes typically results in a human-readable description of the event.
                    </div>
                    <div>
                        Using our example so far, we would have been given a <span class="keyword">Lead Label Applied</span> event.
                    </div>
                </div>
            </div>

            <div class="row mt20">
                <div class="col-md-12">
                    <h6>Basis, Subject, and Action Values</h6>
                    <div>These 3 values are constrained to a predefined set.</div>
                    <div>You can use these values to filter out events that you don't want.</div>

                    <div class="mt15">
                        An event basis will be one of the following values:
                        <ul>
                            <li>Lead - An event regarding a lead</li>
                            <li>Agent - An event regarding an agent in your CRM <i>(not implemented yet)</i></li>
                        </ul>
                    </div>

                    <div class="mt15">
                        An event subject will be one of the following values:
                        <ul>
                            <li>Entity - indicates that the subject of this event is the basis itself</li>
                            <li>Info - any general info about the basis</li>
                            <li>Note - a note was created or changed</li>
                            <li>Label - an existing label was applied or removed</li>
                            <li>
                                Pipeline - a pipeline was altered
                                <br />
                                <span class="ml20 small">Standard pipeline ids are: New Lead, Attempted Contact, Contacted, Appt Set, Showing, Under Contract, Sold</span>
                            </li>
                            <li>Inquiry - an inquiry was made</li>
                            <li>Agent - an agent association with the basis was altered</li>
                            <li>Listing - a property view occurred</li>
                            <li>Search - a search was performed, or a saved search was altered, removed, etc.</li>
                        </ul>
                    </div>

                    <div class="mt15">
                        An event action will be one of the following values:
                        <ul>
                            <li>
                                Created - a new entity was added to the system
                                <br />
                                <span class="example ml20 small"><span class="keyword">Lead Entity Created</span> indicates that a lead was created</span>
                            </li>
                            <li>
                                Deleted - an entity was removed from the system
                                <br />
                                <span class="example ml20 small"><span class="keyword">Lead Note Deleted</span> indicates that a note was deleted</span>
                            </li>
                            <li>
                                Applied - an <i>existing</i> entity was associated with the basis
                                <br />
                                <span class="example ml20 small"><span class="keyword">Lead Label Applied</span> indicates that a label was applied to a lead</span>
                            </li>
                            <li>
                                Removed - an <i>existing</i> entity was disassociated with the basis
                                <br />
                                <span class="example ml20 small"><span class="keyword">Lead Label Removed</span> indicates that a label was removed from a lead</span>
                            </li>
                            <li>
                                Changed - the subject was updated
                                <br />
                                <span class="example ml20 small"><span class="keyword">Lead Info Changed</span> indicates that general information about a lead was changed (name, phone, etc)</span>
                            </li>
                            <li>
                                Occurred - indicates an atomic event that has no substantial data other than the event happened
                                <br />
                                <span class="example ml20 small"><span class="keyword">Lead Search Occurred</span> indicates that a lead performed a search</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <div class="row mt20">
                <div class="col-md-12">
                    <h6>Other Fields</h6>
                    <ul>
                        <li>webhookId - the ID given to the webhook for this token.</li>
                        <li>eventId - a unique ID for this event.</li>
                        <li>eventDate - a <span class="keyword">DateInfo</span> object indicating when the event occurred.</li>
                    </ul>
                </div>
            </div>

            <div class="row mt20">
                <div class="col-md-12">
                    <h6>Sample Payload</h6>
                    <pre class="json">
{
    "webhookId": "ojmc0ujxgidtrkwzitiz5felhurk7qkbb7t27onj3a4c4tc9jqtq1ubsh",
    "eventId": "es4adqqi9l72k4mf7gcskkta5gr948ows002p4f3rbxfxuizdq41n5dt68pefwb2bel8hv200nmsuircxw",
    "basisId": "MM42B1052A5FE644DDA9FA6DE12F8A2232",
    "subjectId": "LLVLZZZT234NSMK0",
    "eventBasis": "Lead",
    "eventSubject": "Label",
    "eventAction": "Applied",
    "eventDate": {
        "dateTime": "2020-06-11T18:16:41.4955239Z"
        "timeZone": "Universal"
    }
}
                    </pre>
                </div>
            </div>

            <div class="row mt20">
                <div class="col-md-12">
                    <h6>Payload Sandbox</h6>
                    <div>Send yourself a sample payload by selecting the options below.</div>

                        <div class="row mt5 form-group">
                            <div class="col-md-6">
                                <input readonly class="form-control" type="text" v-bind:value="sampleWebhookPayload.webhookId" title="Sample Webhook Id" />
                            </div>
                        </div>

                        <div class="row mt5 form-group">
                            <div class="col-md-6">
                                <input readonly class="form-control" type="text" v-bind:value="sampleWebhookPayload.eventId" title="Sample Event Id" />
                            </div>
                        </div>

                        <div class="row mt5 form-group">
                            <div class="col-md-6">
                                <input type="url" class="form-control" placeholder="https://your.callback.url/here" title="Your Webhook URL" v-model="sampleWebhookPayload.url"  />
                            </div>
                        </div>

                        <div class="row mt5 form-group">
                            <div class="col-md-3">
                                <select v-model="sampleWebhookPayload.eventBasis" class="w100 form-control" >
                                    <option selected disabled value="">--- Basis ---</option>
                                    <option>Lead</option>
                                    <option disabled>Agent</option>
                                </select>
                            </div>
                            <div class="col-md-3">
                                <input class="form-control" type="text" v-bind:value="sampleWebhookPayload.basisId" placeholder="--- BasisId ---" />
                            </div>
                        </div>

                        <div class="row mt5 form-group">
                            <div class="col-md-3">
                                <select v-model="sampleWebhookPayload.eventSubject" class="w100 form-control" >
                                    <option selected disabled value="">--- Subject ---</option>
                                    <option>Entity</option>
                                    <option>Info</option>
                                    <option>Note</option>
                                    <option>Label</option>
                                    <option>Pipeline</option>
                                    <option>Inquiry</option>
                                    <option>Agent</option>
                                    <option>Listing</option>
                                    <option>Search</option>
                                </select>
                            </div>
                            <div class="col-md-3">
                                <input class="form-control" type="text" v-bind:value="sampleWebhookPayload.subjectId" placeholder="--- SubjectId ---" />
                            </div>
                        </div>

                        <div class="row mt5 form-group">
                            <div class="col-md-3">
                                <select v-model="sampleWebhookPayload.eventAction" class="w100 form-control" >
                                    <option selected disabled value="">--- Action ---</option>
                                    <option>Created</option>
                                    <option>Deleted</option>
                                    <option>Applied</option>
                                    <option>Removed</option>
                                    <option>Changed</option>
                                    <option>Occurred</option>
                                </select>
                            </div>
                        </div>

                        <div class="row form-group">
                            <div class="col-md-6">
                                <input class="btn btn-primary form-control" value="Send Payload" @click="sendPayload()" />
                            </div>
                        </div>
                </div>
            </div>

        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import { SampleWebhookPayload } from '../../models/SampleWebhookPayload'
export default defineComponent({
    name: "WebHooks",
    setup() {
        const GetGuid = () => {
            return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
                var r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
                return v.toString(16);
            });
        }
        
        let sampleWebhookPayload = new SampleWebhookPayload(GetGuid());
        
        return {
            sampleWebhookPayload
        }
    },
    methods: {
        sendPayload() {
            this.$store.state.axiosService.PostSampleWebhookPayload(this.sampleWebhookPayload);
        }
    }
})
</script>
